import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Search, Filter, TrendingUp, Star, Clock, DollarSign, Tag, X, Loader2, List, MoreVertical, Settings } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SearchBar } from '../components/ui/SearchBar';
import { MenuItemFormModal } from '../components/MenuItemFormModal';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
export function MenuView() {
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [sortBy, setSortBy] = useState('name');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSectionsItem, setEditingSectionsItem] = useState<any>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();

  // Load data from Supabase on mount
  useEffect(() => {
    loadData();
  }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      if (isSupabaseConfigured()) {
        // Load from Supabase
        const [items, cats] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getMenuCategories()]);
        setMenuItems(items || []);
        setCategories(cats.length > 0 ? cats : ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']);
        console.log('✅ Loaded', items.length, 'menu items from Supabase');
      } else {
        // Fallback to localStorage only
        const savedItems = localStorage.getItem('criket_menu_items');
        const savedCats = localStorage.getItem('criket_menu_categories');
        setMenuItems(savedItems ? JSON.parse(savedItems) : []);
        setCategories(savedCats ? JSON.parse(savedCats) : ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']);
      }
    } catch (error) {
      console.error('Error loading menu data:', error);
      // Fallback to localStorage
      const savedItems = localStorage.getItem('criket_menu_items');
      const savedCats = localStorage.getItem('criket_menu_categories');
      setMenuItems(savedItems ? JSON.parse(savedItems) : []);
      setCategories(savedCats ? JSON.parse(savedCats) : ['Platos Principales', 'Entradas', 'Ensaladas', 'Pastas', 'Sandwiches']);
    } finally {
      setLoading(false);
    }
  };
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showToast('warning', 'Campo requerido', 'Por favor ingresa un nombre para la categoría');
      return;
    }
    if (categories.includes(newCategoryName.trim())) {
      showToast('warning', 'Categoría existente', 'Esta categoría ya existe');
      return;
    }
    const categoryName = newCategoryName.trim();
    try {
      if (isSupabaseConfigured()) {
        await supabaseClient.createMenuCategory(categoryName);
      }
      const newCategories = [...categories, categoryName];
      setCategories(newCategories);
      // Save to localStorage as backup
      localStorage.setItem('criket_menu_categories', JSON.stringify(newCategories));
      addLog('Categoría Creada', categoryName, 'menu');
      showToast('success', 'Categoría creada', `"${categoryName}" agregada exitosamente`);
      setNewCategoryName('');
      setShowCategoryModal(false);
    } catch (error) {
      console.error('Error creating category:', error);
      showToast('error', 'Error', 'No se pudo crear la categoría');
    }
  };
  const handleDeleteCategory = (category: string) => {
    const itemsInCategory = menuItems.filter(item => item.category === category);
    if (itemsInCategory.length > 0) {
      showToast('warning', 'Categoría en uso', `Hay ${itemsInCategory.length} plato(s) en esta categoría. Elimínalos primero.`);
      return;
    }
    setCategoryToDelete(category);
  };
  const confirmDeleteCategory = async () => {
    if (categoryToDelete) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deleteMenuCategory(categoryToDelete);
        }
        const newCategories = categories.filter(c => c !== categoryToDelete);
        setCategories(newCategories);
        // Save to localStorage as backup
        localStorage.setItem('criket_menu_categories', JSON.stringify(newCategories));
        addLog('Categoría Eliminada', categoryToDelete, 'menu');
        showToast('success', 'Categoría eliminada', `"${categoryToDelete}" ha sido eliminada`);
        setCategoryToDelete(null);
        if (selectedCategory === categoryToDelete) {
          setSelectedCategory('Todos');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        showToast('error', 'Error', 'No se pudo eliminar la categoría');
      }
    }
  };
  const handleAddMenuItem = async (newItem: any) => {
    try {
      if (editingItem) {
        // Update existing item
        const updatedItem = {
          ...newItem,
          price: parseFloat(newItem.price)
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.updateMenuItem(editingItem.id, updatedItem);
        }
        const updatedItems = menuItems.map(item => item.id === editingItem.id ? {
          ...item,
          ...updatedItem
        } : item);
        setMenuItems(updatedItems);
        // Save to localStorage as backup
        localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
        addLog('Plato Actualizado', `${newItem.name} - ${newItem.category}`, 'menu');
        showToast('success', 'Plato actualizado', `"${newItem.name}" ha sido actualizado`);
        setEditingItem(null);
      } else {
        // Create new item
        const item = {
          ...newItem,
          id: `menu-${Date.now()}`,
          price: parseFloat(newItem.price)
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.createMenuItem(item);
        }
        const updatedItems = [...menuItems, item];
        setMenuItems(updatedItems);
        // Save to localStorage as backup
        localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
        addLog('Plato Creado', `${newItem.name} - ${newItem.category}`, 'menu');
        showToast('success', 'Plato creado', `"${newItem.name}" ha sido agregado al menú`);
      }
    } catch (error) {
      console.error('Error saving menu item:', error);
      showToast('error', 'Error', 'No se pudo guardar el plato');
    }
  };
  const handleEdit = (item: any) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setDeleteConfirm(item);
  };
  const handleEditSections = (item: any) => {
    setEditingSectionsItem(item);
  };
  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deleteMenuItem(deleteConfirm.id);
        }
        const updatedItems = menuItems.filter(item => item.id !== deleteConfirm.id);
        setMenuItems(updatedItems);
        // Save to localStorage as backup
        localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
        addLog('Plato Eliminado', `${deleteConfirm.name} - ${deleteConfirm.category}`, 'menu');
        showToast('success', 'Plato eliminado', `"${deleteConfirm.name}" ha sido eliminado`);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting menu item:', error);
        showToast('error', 'Error', 'No se pudo eliminar el plato');
      }
    }
  };
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'Todos' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'category':
        return a.category.localeCompare(b.category);
      default:
        return 0;
    }
  });
  const stats = {
    total: menuItems.length,
    almuerzo: menuItems.filter(i => i.catalogType === 'Almuerzo').length,
    cena: menuItems.filter(i => i.catalogType === 'Cena').length,
    avgPrice: menuItems.reduce((sum, i) => sum + i.price, 0) / menuItems.length || 0
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-1896">
        <div className="text-center" data-id="element-1897">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500 mx-auto mb-4" data-id="element-1898" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1899">Cargando menú...</p>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-1900">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-1901">
        <div data-id="element-1902">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1903">
            Gestión de Menú
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1904">
            Administra los platos y categorías disponibles
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1905">
          <Button variant="outline" leftIcon={<Tag size={18} data-id="element-1907" />} onClick={() => setShowCategoryModal(true)} data-id="element-1906">
            Gestionar Categorías
          </Button>
          <Button leftIcon={<Plus size={18} data-id="element-1909" />} onClick={() => {
          setEditingItem(null);
          setIsModalOpen(true);
        }} data-id="element-1908">
            Nuevo Plato
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-1910">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800" data-id="element-1911">
          <div className="flex items-center justify-between" data-id="element-1912">
            <div data-id="element-1913">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1" data-id="element-1914">
                Total Platos
              </p>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-100" data-id="element-1915">
                {stats.total}
              </p>
            </div>
            <div className="p-3 bg-blue-200 dark:bg-blue-800 rounded-lg" data-id="element-1916">
              <TrendingUp className="text-blue-700 dark:text-blue-300" size={24} data-id="element-1917" />
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800" data-id="element-1918">
          <div className="flex items-center justify-between" data-id="element-1919">
            <div data-id="element-1920">
              <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-1" data-id="element-1921">
                Almuerzo
              </p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-100" data-id="element-1922">
                {stats.almuerzo}
              </p>
            </div>
            <div className="p-3 bg-green-200 dark:bg-green-800 rounded-lg" data-id="element-1923">
              <Clock className="text-green-700 dark:text-green-300" size={24} data-id="element-1924" />
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800" data-id="element-1925">
          <div className="flex items-center justify-between" data-id="element-1926">
            <div data-id="element-1927">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1" data-id="element-1928">
                Cena
              </p>
              <p className="text-3xl font-bold text-purple-900 dark:text-purple-100" data-id="element-1929">
                {stats.cena}
              </p>
            </div>
            <div className="p-3 bg-purple-200 dark:bg-purple-800 rounded-lg" data-id="element-1930">
              <Star className="text-purple-700 dark:text-purple-300" size={24} data-id="element-1931" />
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-800" data-id="element-1932">
          <div className="flex items-center justify-between" data-id="element-1933">
            <div data-id="element-1934">
              <p className="text-xs text-orange-600 dark:text-orange-400 font-medium mb-1" data-id="element-1935">
                Precio Promedio
              </p>
              <p className="text-3xl font-bold text-orange-900 dark:text-orange-100" data-id="element-1936">
                {stats.total > 0 ? `RD$ ${stats.avgPrice.toFixed(0)}` : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-orange-200 dark:bg-orange-800 rounded-lg" data-id="element-1937">
              <DollarSign className="text-orange-700 dark:text-orange-300" size={24} data-id="element-1938" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 mb-6" data-id="element-1939">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-1940">
          {/* Search */}
          <div className="flex-1" data-id="element-1941">
            <SearchBar placeholder="Buscar platos..." onSearch={setSearchTerm} className="w-full" data-id="element-1942" />
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide justify-start lg:justify-center" data-id="element-1943">
            <button onClick={() => setSelectedCategory('Todos')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === 'Todos' ? 'bg-black dark:bg-white text-white dark:text-black shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-1944">
              Todos
            </button>
            {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === category ? 'bg-black dark:bg-white text-white dark:text-black shadow-md' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-1945">
                {category}
              </button>)}
          </div>

          {/* Sort */}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white" data-id="element-1946">
            <option value="name" data-id="element-1947">Nombre A-Z</option>
            <option value="price-low" data-id="element-1948">Precio: Menor a Mayor</option>
            <option value="price-high" data-id="element-1949">Precio: Mayor a Menor</option>
            <option value="category" data-id="element-1950">Categoría</option>
          </select>
        </div>
      </div>

      {/* Menu Grid */}
      {filteredItems.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-1951">
          <Search size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-1952" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-1953">
            {menuItems.length === 0 ? 'No hay platos en el menú' : 'No se encontraron platos'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-2" data-id="element-1954">
            {menuItems.length === 0 ? 'Agrega tu primer plato al menú' : searchTerm ? 'Intenta con otra búsqueda' : 'Ajusta los filtros para ver más resultados'}
          </p>
        </div> :
    // Grid ajustado: 2 columnas fijas en movil, sin justify-items-center
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-6" data-id="element-1955">
          {filteredItems.map(item => <Card key={item.id} className="group hover:border-gray-400 dark:hover:border-gray-600 transition-all hover:shadow-lg w-full overflow-hidden" noPadding data-id="element-1956">
              <div className="relative h-28 sm:h-48 overflow-hidden bg-gray-100 dark:bg-gray-800" data-id="element-1957">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-1958" />
                <div className="absolute top-2 right-2 flex gap-2" data-id="element-1959">
                  <span className="px-1.5 py-0.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur text-gray-900 dark:text-white text-[10px] sm:text-xs font-bold rounded-full border border-gray-200 dark:border-gray-700 shadow-lg" data-id="element-1960">
                    {item.catalogType}
                  </span>
                </div>
                <div className="absolute bottom-2 left-2" data-id="element-1961">
                  <span className="px-2 py-0.5 bg-black/80 dark:bg-white/90 backdrop-blur text-white dark:text-black text-xs sm:text-sm font-bold rounded-full" data-id="element-1962">
                    ${item.price.toFixed(0)}
                  </span>
                </div>
              </div>

              <div className="p-2 sm:p-5 flex flex-col h-auto" data-id="element-1963">
                <div className="mb-2" data-id="element-1964">
                  <h3 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white mb-1 line-clamp-1 leading-tight" data-id="element-1965">
                    {item.name}
                  </h3>
                  <span className="text-[10px] sm:text-xs font-medium px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-300 inline-block truncate max-w-full" data-id="element-1966">
                    {item.category}
                  </span>
                </div>

                <p className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 min-h-[40px]" data-id="element-1967">
                  {item.description}
                </p>

                <div className="flex gap-2 flex-col sm:flex-row mt-auto" data-id="element-1968">
                  <Button variant="outline" size="sm" fullWidth onClick={() => handleEdit(item)} className="h-8 text-xs sm:text-sm px-2" data-id="element-1969">
                    <Edit2 size={12} className="mr-1 sm:mr-2" data-id="element-1970" />
                    <span className="inline" data-id="element-1971">Edit</span>
                  </Button>
                  <button onClick={() => handleDelete(item)} className="h-8 flex justify-center items-center text-red-600 dark:text-red-400 bg-red-50 hover:bg-red-100 dark:bg-red-900/10 dark:hover:bg-red-900/20 rounded-md transition-colors w-full sm:w-auto sm:px-3" title="Eliminar" data-id="element-1972">
                    <Trash2 size={14} data-id="element-1973" />
                  </button>
                </div>
              </div>
            </Card>)}
        </div>}

      {/* Result Count */}
      {filteredItems.length > 0 && <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-1974">
          Mostrando {filteredItems.length} de {menuItems.length} platos
        </div>}

      {/* Menu Item Modal */}
      <MenuItemFormModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingItem(null);
    }} onSave={handleAddMenuItem} editingItem={editingItem} availableCategories={categories} data-id="element-1975" />

      {/* Category Management Modal */}
      {showCategoryModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-1976">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-1977">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1978">
              <div className="flex items-center justify-between" data-id="element-1979">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1980">
                  Gestionar Categorías
                </h2>
                <button onClick={() => setShowCategoryModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-1981">
                  <X size={20} data-id="element-1982" />
                </button>
              </div>
            </div>

            <div className="p-6" data-id="element-1983">
              <div className="mb-6" data-id="element-1984">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1985">
                  Nueva Categoría
                </label>
                <div className="flex gap-2" data-id="element-1986">
                  <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddCategory()} placeholder="Ej: Postres, Bebidas..." className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-1987" />
                  <Button onClick={handleAddCategory} leftIcon={<Plus size={16} data-id="element-1989" />} data-id="element-1988">
                    Agregar
                  </Button>
                </div>
              </div>

              <div data-id="element-1990">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-1991">
                  Categorías Actuales ({categories.length})
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto" data-id="element-1992">
                  {categories.map(category => {
                const itemCount = menuItems.filter(item => item.category === category).length;
                return <div key={category} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-1993">
                        <div data-id="element-1994">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-1995">
                            {category}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1996">
                            {itemCount} plato{itemCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteCategory(category)} className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-id="element-1997">
                          <Trash2 size={16} data-id="element-1998" />
                        </button>
                      </div>;
              })}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-1999">
              <Button onClick={() => setShowCategoryModal(false)} fullWidth data-id="element-2000">
                Cerrar
              </Button>
            </div>
          </div>
        </div>}

      {/* Delete Category Confirmation */}
      {categoryToDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2001">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-2002">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-2003">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2004">
                Eliminar Categoría
              </h2>
            </div>
            <div className="p-6" data-id="element-2005">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-2006">
                ¿Estás seguro que deseas eliminar la categoría{' '}
                <strong data-id="element-2007">"{categoryToDelete}"</strong>?
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-2008">
              <Button variant="outline" onClick={() => setCategoryToDelete(null)} fullWidth data-id="element-2009">
                Cancelar
              </Button>
              <Button onClick={confirmDeleteCategory} fullWidth className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-2010">
                Eliminar
              </Button>
            </div>
          </div>
        </div>}

      {/* Delete Menu Item Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2011">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-2012">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-2013">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-2014">
                Eliminar Plato
              </h2>
            </div>
            <div className="p-6" data-id="element-2015">
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-2016">
                ¿Estás seguro que deseas eliminar{' '}
                <strong data-id="element-2017">{deleteConfirm.name}</strong>? Esta acción no se puede
                deshacer.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-2018">
                <img src={deleteConfirm.image} alt={deleteConfirm.name} className="w-full h-32 object-cover rounded-lg mb-2" data-id="element-2019" />
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2020">
                  <strong data-id="element-2021">Categoría:</strong> {deleteConfirm.category}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2022">
                  <strong data-id="element-2023">Precio:</strong> RD$ {deleteConfirm.price.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-2024">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-2025">
                Cancelar
              </Button>
              <Button onClick={confirmDelete} fullWidth className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-2026">
                Eliminar Plato
              </Button>
            </div>
          </div>
        </div>}

      {/* Menu Section Editor Modal */}
      {editingSectionsItem && <MenuSectionEditor menuItemId={editingSectionsItem.id} menuItemName={editingSectionsItem.name} isOpen={!!editingSectionsItem} onClose={() => setEditingSectionsItem(null)} data-id="element-2027" />}
    </div>;
}